// The authoritative write (szzt-spec.md §7, §8, §15).
//
// publish() runs the write half of "build then write": upload every resource blob (verifying its
// CID), precompute the manifest- and deployment-record CIDs client-side so the strong refs are
// correct, then create the `ing.dasl.masl`, `app.szzt.deployment`, and `app.szzt.site` records in
// ONE atomic `applyWrites`. The site pointer is guarded so concurrent writers serialize.
//
// Spec/API note: §7.1 specifies guarding the site update with per-record `swapRecord`, but
// `com.atproto.repo.applyWrites` offers no per-write swap — only a top-level `swapCommit`. We
// guard the whole atomic write with `swapCommit` (the repo head): correct and slightly more
// conservative (any concurrent repo write triggers a re-read + retry, never a torn/regressed
// pointer). Rollback, a single-record write, uses true per-record `swapRecord` (see rollback.ts).
import type { BuildResult } from "@szzt/core";
import type { FetchedRecord } from "@szzt/records";
import { recomputeRootCid } from "@szzt/records";
import { uploadBlobs } from "./blobs.ts";
import { BlobReadinessError, cleanupBlobStages, prepareBlobs, requireBlobReader, verifyBlobReadiness } from "./blob-readiness.ts";
import { inspectPublishedChain } from "./readback.ts";
import type { ExpectedPublish } from "./readback.ts";
import {
  atUri,
  buildDeploymentRecord,
  buildMaslRecord,
  buildSiteRecord,
  COLLECTION,
  DEFAULT_ENTRY,
  recordCid,
} from "./records.ts";
import { nextTid } from "./tid.ts";
import {
  type ApplyWrite,
  type CleanupOutcome,
  type MirrorProducer,
  type PdsClient,
  type PublishInput,
  type PublishResult,
  type SiteMeta,
  type StrongRef,
  ManifestIntegrityError,
  ManifestTooLargeError,
  MAX_RECORD_BYTES,
  PublishVerificationError,
  SwapFailedError,
} from "./types.ts";

/**
 * Merge caller-supplied metadata over an existing site record's fields (caller wins per field).
 * `extraHints` (from a successful mirror push) are unioned onto the base hints — a fresh producer
 * hint adds to, never replaces, what the caller/prior record already advertised; exact duplicates
 * are dropped, array order preserved.
 */
function mergeSiteMeta(existing: unknown, meta: SiteMeta, extraHints: readonly string[] = []): SiteMeta {
  const ex = (existing ?? {}) as Partial<SiteMeta>;
  const out: SiteMeta = {};
  const title = meta.title ?? ex.title;
  const slug = meta.slug ?? ex.slug;
  const hints = dedupe([...(meta.hints ?? ex.hints ?? []), ...extraHints]);
  if (title !== undefined) out.title = title;
  if (slug !== undefined) out.slug = slug;
  if (hints.length > 0) out.hints = hints;
  return out;
}

/** Order-preserving dedupe of exact string entries. */
function dedupe(items: readonly string[]): string[] {
  return [...new Set(items)];
}

/**
 * Best-effort BYO-producer push (§7.3): store every resource blob AND the manifest to an external
 * mirror, then return the producer's hints — but ONLY if every push succeeded. All-or-nothing: one
 * failed block means the mirror is missing content, so we advertise NOTHING for it (a partial hint
 * would just waste a gateway's racing budget). Never throws — a mirror is a performance aid; the
 * authoritative PDS publish must not fail because a CDN/IPFS push did.
 */
async function pushToMirror(producer: MirrorProducer, built: BuildResult): Promise<string[]> {
  try {
    for (const blob of built.blobs) {
      await producer.pushBlock({
        cid: blob.cid,
        bytes: blob.bytes,
        codec: "raw",
        ...(blob.blobMimeType !== undefined ? { contentType: blob.blobMimeType } : {}),
      });
    }
    // The manifest is content-addressed too (root CID = dagCbor(tile)) but is NOT in `built.blobs`
    // (it lives as a record, not a PDS blob), so an IPFS mirror of the root CID needs it pushed here.
    await producer.pushBlock({ cid: built.rootCid, bytes: built.encodedManifest, codec: "dag-cbor" });
    return producer.hints();
  } catch {
    return [];
  }
}

/**
 * Push to every configured mirror INDEPENDENTLY (§7.3) and union the hints of those that fully
 * succeeded. Each producer's push is its own all-or-nothing ({@link pushToMirror}), so one mirror
 * being down or incomplete drops only its own hint — a working CDN's hint is never lost because a
 * flaky IPFS node happened to be configured alongside it. Order-preserving across producers.
 */
async function pushToMirrors(producers: readonly MirrorProducer[], built: BuildResult): Promise<string[]> {
  const hints: string[] = [];
  for (const producer of producers) hints.push(...(await pushToMirror(producer, built)));
  return hints;
}

/**
 * Publish (or update) a site by writing its blobs and records to `client`'s repo (§7).
 * Publish is the first update; `update` is the same call with `siteRkey`/`prevManifestCid` set (§8).
 * The caller MUST have already run `@szzt/core`'s `build` and verified `built.rootCid` (§15).
 */
export async function publish(input: PublishInput): Promise<PublishResult> {
  const {
    client,
    built,
    siteRkey,
    prevManifestCid,
    entry = DEFAULT_ENTRY,
    meta = {},
    maxRetries = 3,
  } = input;
  const now = input.now ?? (() => new Date());
  const genTid = input.genTid ?? nextTid;
  const { did } = client;

  // 0. Fail-closed integrity guard (§9.2), BEFORE any side effect: the manifest record's declared
  //    root CID (`built.rootCid`, written as the record `cid`) MUST equal dagCbor(its own tile)
  //    (`built.manifest`). A conforming `build()` guarantees this, but a caller that mixed fields
  //    from two builds (e.g. a stale UI keeping an old rootCid) would otherwise persist a self-
  //    inconsistent record that every gateway rejects at serve time (→ permanent 502). Refuse it.
  const recomputedRoot = await recomputeRootCid(built.manifest);
  if (recomputedRoot !== built.rootCid) {
    throw new ManifestIntegrityError(recomputedRoot, built.rootCid);
  }

  // 0b. §3.7 fail-fast, ALSO before any side effect. The MASL is inline in the `ing.dasl.masl` record,
  //     so an over-limit manifest is rejected by the PDS at `applyWrites` (step 3) — but only AFTER
  //     every resource blob has been uploaded (step 1), the exact wasteful late failure §3.7 legislates
  //     against. Validate the encoded manifest size against the target PDS's record-size limit HERE, so a
  //     path-heavy site fails immediately with a clear error and no blob is uploaded. `build()` already
  //     computes `encodedManifestSize`; this is its first non-test consumer (closes gap G10).
  const maxRecordBytes = input.maxRecordBytes ?? MAX_RECORD_BYTES;
  if (built.encodedManifestSize > maxRecordBytes) {
    throw new ManifestTooLargeError(built.encodedManifestSize, maxRecordBytes);
  }

  // 1. Ordinary publishing keeps its three-record commit. The opt-in verified path first
  //    anchors bounded batches in an inert collection (new uploads may otherwise be temporary).
  const prepared = input.verifyBlobs ? await prepareBlobs(client, built) : undefined;
  if (prepared === undefined) await uploadBlobs(client, built);

  // 1b. BYO producers (§7.3): best-effort push every content block to each external CDN/IPFS mirror.
  //     Runs ONCE (not per swapCommit retry). Returns the hints to advertise, per-mirror all-or-
  //     nothing; a mirror's failure yields no hint for it and never blocks the authoritative write.
  const producerHints = input.producers ? await pushToMirrors(input.producers, built) : [];

  // 2. Precompute rkeys, record values, and their atproto CIDs (§7.2). Generated once and reused
  //    across `swapCommit` retries — a failed atomic write persists nothing, so re-creating the
  //    same deployment/manifest records under the same rkeys is safe and idempotent.
  const createdAt = now().toISOString();
  const maslRkey = genTid();
  const deployRkey = genTid();
  // Minted (or reused) before the deployment record so the deployment can back-reference its site
  // rkey (§4.2 `site`), letting a repo's append-only deployments be grouped by site for history.
  const finalSiteRkey = siteRkey ?? genTid();

  const maslRecord = buildMaslRecord(built, createdAt, prevManifestCid);
  const tileCid = await recordCid(maslRecord);
  const tileUri = atUri(did, COLLECTION.masl, maslRkey);

  const deployRecord = buildDeploymentRecord(
    built,
    { uri: tileUri, cid: tileCid },
    entry,
    createdAt,
    finalSiteRkey,
  );
  const deploymentCid = await recordCid(deployRecord);
  const deploymentUri = atUri(did, COLLECTION.deployment, deployRkey);
  const current: StrongRef = { uri: deploymentUri, cid: deploymentCid };

  const siteUri = atUri(did, COLLECTION.site, finalSiteRkey);

  // 3. Atomic, swapCommit-guarded write with re-read + retry on conflict (§7.1).
  //    Capture the site record as it existed BEFORE the write, so a failed read-back verification
  //    (step 4) can restore it (update) or delete our new one (first publish).
  let sitePriorToWrite: FetchedRecord | null = null;
  let installedSite: ExpectedPublish["site"];
  for (let attempt = 0; ; attempt++) {
    if (prepared !== undefined) {
      requireBlobReader(client);
      try {
        await verifyBlobReadiness(client, built);
      } catch (cause) {
        throw new BlobReadinessError("canonical PDS blob retrieval failed before the site switch", prepared.stages, cause);
      }
    }
    const head = await client.getLatestCommit();

    // Preserve the stable site identity across deployments: on update, keep the existing
    // record's `createdAt` and carry forward metadata the caller did not override (§4.1).
    const existing = siteRkey !== undefined ? await client.getRecord(COLLECTION.site, finalSiteRkey) : null;
    const siteCreatedAt =
      existing !== null ? recordCreatedAt(existing.value) ?? createdAt : createdAt;
    const siteRecord = buildSiteRecord(
      current,
      siteCreatedAt,
      mergeSiteMeta(existing?.value, meta, producerHints),
    );
    const siteCid = await recordCid(siteRecord);
    const siteAction: "create" | "update" = existing !== null ? "update" : "create";

    const writes: ApplyWrite[] = [
      { action: "create", collection: COLLECTION.masl, rkey: maslRkey, value: maslRecord },
      { action: "create", collection: COLLECTION.deployment, rkey: deployRkey, value: deployRecord },
      { action: siteAction, collection: COLLECTION.site, rkey: finalSiteRkey, value: siteRecord },
    ];

    try {
      await client.applyWrites({ writes, swapCommit: head.cid });
      sitePriorToWrite = existing;
      installedSite = { uri: siteUri, rkey: finalSiteRkey, cid: siteCid, value: siteRecord };
      break;
    } catch (err) {
      if (err instanceof SwapFailedError && attempt < maxRetries) continue;
      throw err;
    }
  }

  // 4. Inspect the COMPLETE installed chain, including returned wire CIDs, both strong refs,
  //    authored values and the manifest root. This is PDS readback, not signed MST verification.
  const readback = await inspectPublishedChain(client, {
    manifest: { uri: tileUri, rkey: maslRkey, cid: tileCid, value: maslRecord },
    deployment: { uri: deploymentUri, rkey: deployRkey, cid: deploymentCid, value: deployRecord },
    site: installedSite,
    rootCid: built.rootCid,
  });
  if (readback.problem !== null) {
    const cleanup = await rollbackFailedPublish(
      client,
      maslRkey,
      deployRkey,
      finalSiteRkey,
      deploymentUri,
      installedSite.cid,
      sitePriorToWrite,
      maxRetries,
    );
    throw new PublishVerificationError(readback.problem, cleanup);
  }

  return {
    deploymentUri,
    deploymentCid,
    tileUri,
    tileCid,
    siteUri,
    siteRkey: finalSiteRkey,
    rootCid: built.rootCid,
    ...(readback.superseded ? { superseded: true as const } : {}),
    ...(prepared !== undefined ? {
      blobVerification: {
        uploaded: prepared.uploaded,
        reused: prepared.reused,
        verified: true as const,
        stagingCleanup: await cleanupBlobStages(client, prepared.stages),
      },
    } : {}),
  };
}

/** Read a record value's `createdAt`, if present and a string. */
function recordCreatedAt(value: unknown): string | undefined {
  if (value !== null && typeof value === "object" && "createdAt" in value) {
    const c = (value as { createdAt: unknown }).createdAt;
    if (typeof c === "string") return c;
  }
  return undefined;
}

/** Read a site record value's `current.uri`, if present and well-formed. */
function siteCurrentUri(value: unknown): string | undefined {
  if (value === null || typeof value !== "object" || !("current" in value)) return undefined;
  const current = (value as { current: unknown }).current;
  if (current === null || typeof current !== "object" || !("uri" in current)) return undefined;
  const uri = (current as { uri: unknown }).uri;
  return typeof uri === "string" ? uri : undefined;
}

/**
 * Undo a publish whose read-back verification failed: delete the new manifest + deployment records,
 * and either delete the site record we created (first publish) or restore it to its exact
 * pre-publish value (update — repoints back to the prior deployment).
 *
 * GUARDED, like every other write on this path (§7.1). This is a cleanup, not a fresh publish, so
 * it must never win a race it did not enter: between our own guarded write landing and this call, a
 * concurrent publisher can have moved `site.current` on to THEIR deployment. Restoring our captured
 * pre-write value on top of that would silently revert them (potentially several versions), and
 * deleting our manifest could dangle a `prev` they chained onto it. So we re-read the site record
 * and act ONLY while its wire CID still equals our own site's CID AND `current` names our own
 * deployment. Metadata edits count too. Otherwise we no-op and report retained records; these may
 * still be live, so do not promise they are unreferenced or safe to prune.
 *
 * Note on the guard: §7.1 and the H6 review both describe per-record `swapRecord` on the site
 * record, but `applyWrites` exposes no per-write swap — only a top-level `swapCommit` (see this
 * file's header). `swapCommit` on a freshly-read head is what's available and is strictly stronger:
 * it guards the manifest/deployment deletes too, not just the site pointer, and any concurrent repo
 * write sends us back through the re-read (where the `current` check is what actually decides).
 * Matches `publish`'s own re-read + retry idiom above.
 */
async function rollbackFailedPublish(
  client: PdsClient,
  maslRkey: string,
  deployRkey: string,
  siteRkey: string,
  ourDeploymentUri: string,
  ourSiteCid: string,
  sitePriorToWrite: FetchedRecord | null,
  maxRetries: number,
): Promise<CleanupOutcome> {
  for (let attempt = 0; ; attempt++) {
    try {
      const head = await client.getLatestCommit();
      const live = await client.getRecord(COLLECTION.site, siteRkey);
      const liveCurrentUri = live === null ? undefined : siteCurrentUri(live.value);
      if (liveCurrentUri !== ourDeploymentUri || live?.cid !== ourSiteCid) {
        return {
          status: "skipped",
          reason: "the site record changed or disappeared after our write; restoring it could overwrite a concurrent edit",
        };
      }

      const writes: ApplyWrite[] = [
        { action: "delete", collection: COLLECTION.masl, rkey: maslRkey },
        { action: "delete", collection: COLLECTION.deployment, rkey: deployRkey },
      ];
      if (sitePriorToWrite === null) {
        writes.push({ action: "delete", collection: COLLECTION.site, rkey: siteRkey });
      } else {
        writes.push({ action: "update", collection: COLLECTION.site, rkey: siteRkey, value: sitePriorToWrite.value });
      }
      await client.applyWrites({ writes, swapCommit: head.cid });
      return { status: "undone" };
    } catch (err) {
      if (err instanceof SwapFailedError && attempt < maxRetries) continue;
      // Reported, never swallowed and never rethrown: the caller must still see the originating
      // PublishVerificationError (it explains WHY we are here), but a cleanup that failed — or one
      // that stood down for a concurrent writer — changes what is left in the repo, so it rides
      // along on that error rather than vanishing into a bare `catch {}`.
      return { status: "failed", error: err };
    }
  }
}

/** Re-export so callers can reference the input/result types alongside the function. */
export type { PublishInput, PublishResult, PdsClient, BuildResult };
